import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, UseFormReturn } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";
import { ContactInfoFields } from "@/components/bid-request/ContactInfoFields";
import { EventLocationFields } from "@/components/bid-request/EventLocationFields";
import { EventDetailsFields } from "@/components/bid-request/EventDetailsFields";
import { AdditionalDetailsFields } from "@/components/bid-request/AdditionalDetailsFields";
import { ReferralCodeField } from "@/components/bid-request/ReferralCodeField";
// import { BidRequestFormValues } from "@/components/bid-request/types";
import { useRef, useState } from "react";
import axios, { AxiosResponse } from "axios";
import { Loading } from "@/components/ui/loading";
import { BidFormValues } from "@/components/admin/bids/types";
import { axiosInstance } from "./auth/auth.context";
import { BidFormSchema } from "@/components/admin/bids/schemas";
import { useSearchParams } from "react-router-dom";
import { useReferralCode } from "@/hooks/useReferralCode";
import { useFullDates } from "@/hooks/useFullDates";
import { useNavigate } from "react-router-dom";

export default function BidRequest() {
  const { toast } = useToast();
  const [loading, setLoading] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { referralState, handleManualReferralChange, getSecureReferralCode } =
    useReferralCode(searchParams);

  // Advisory only: a failure here resolves to an empty list and the form behaves
  // exactly as it did before the notice existed.
  const fullDates = useFullDates();

  // Spam signals. Neither is ever shown to the customer, and the backend treats
  // their absence as human, so they cannot block a real submission.
  const renderedAt = useRef(performance.now());
  const honeypotRef = useRef<HTMLInputElement>(null);

  const form: UseFormReturn<BidFormValues> = useForm<BidFormValues>({
    resolver: zodResolver(BidFormSchema),
    defaultValues: {
      contactName: "",
      companyName: "",
      email: "",
      phoneNumber: "",
      address: "",
      city: "",
      nameOfLocation: "",
      startTime: new Date(),
      deliveryInfo: "",
      interestedGames: "",
      wantUsToDoRaffle: "NO",
      foodInfo: "",
      additionalInfo: "",
      referralCode: "",
      positions: {},
      setupTime: "",
      parkingInfo: "",
    } as BidFormValues,
  });

  async function onSubmit(values: BidFormValues) {
    setLoading(true);

    // Get the secure referral code (prioritizes URL over manual entry)
    const secureReferralCode = getSecureReferralCode();

    const startTimeValidated =
      values.startTime instanceof Date
        ? values.startTime.toISOString()
        : undefined;

    const route = process.env.REACT_APP_BASE_API_URL + "event";

    // firmnessLevel is deliberately absent. Customers are no longer asked how
    // firmly dealers should play -- it is an operational call an admin makes
    // when pricing the bid -- so the event arrives with it unset rather than
    // carrying a value nobody chose.
    const data = {
      contactName: values.contactName,
      companyName: values.companyName,
      email: values.email,
      phoneNumber: values.phoneNumber,
      address: values.address,
      city: values.city,
      nameOfLocation: values.nameOfLocation,
      startTime: startTimeValidated,
      eventLength: values.eventLength,
      attendees: values.attendees,
      deliveryInfo: values.deliveryInfo,
      interestedGames: values.interestedGames,
      foodInfo: values.foodInfo,
      dealerSpecialInstructions: values.dealerSpecialInstructions,
      additionalInfo: values.additionalInfo,
      extraPayPerDealer: values.extraPayPerDealer,
      additionalFee: values.additionalFee,
      discountAmount: values.discountAmount,
      positionQuantities: Object.fromEntries(
        Object.entries(values.positions || {}).map(([key, value]) => [
          key,
          value.quantity,
        ]),
      ),
      hasRaffle: values.hasRaffle,
      wantUsToDoRaffle: values.wantUsToDoRaffle,
      referralCode: secureReferralCode,
      honeypot: honeypotRef.current?.value ?? "",
      formFillMs: Math.round(performance.now() - renderedAt.current),
    };

    try {
      const response: AxiosResponse = await axiosInstance.post(route, data);

      if (response.status === 200 || response.status === 201) {
        form.reset();

        toast({
          title: "Bid Request Received",
          description:
            "Thank you for your interest. A member of our staff will contact you shortly.",
          variant: "success",
        });
        navigate("/bid-request-success");
      }
    } catch (error) {
      console.error("ERROR SUBMITTING FORM", error);
      const tooMany =
        axios.isAxiosError(error) && error.response?.status === 429;
      toast({
        title: "Error",
        description: tooMany
          ? "We have received several requests from your connection recently. Please try again in an hour."
          : "Something went wrong. Please try again later. " + error,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container max-w-2xl mx-auto py-10 px-4">
      <h1 className="text-3xl font-bold text-foreground mb-8">Request a Bid</h1>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-6">
            <ContactInfoFields form={form} />
            <EventLocationFields form={form} />
            <EventDetailsFields form={form} fullDates={fullDates} />
            <AdditionalDetailsFields form={form} showFirmnessLevel={false} />
            <ReferralCodeField
              control={form.control}
              name="referralCode"
              referralState={referralState}
              onReferralChange={handleManualReferralChange}
            />
          </div>

          {/*
            Honeypot. Moved off screen rather than display:none, which some bots
            skip. Hidden from screen readers, out of the tab order, and marked
            for password managers to ignore so autofill never fills it.
          */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-10000px",
              top: "auto",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          >
            <label htmlFor="bid-extra-notes">Leave this field empty</label>
            <input
              id="bid-extra-notes"
              name="extraNotes"
              type="text"
              ref={honeypotRef}
              tabIndex={-1}
              autoComplete="off"
              defaultValue=""
              data-1p-ignore="true"
              data-lpignore="true"
              data-bwignore="true"
              data-form-type="other"
            />
          </div>

          {loading ? (
            <Loading />
          ) : (
            <Button
              type="submit"
              className="w-full"
              disabled={referralState.isValidating}
            >
              {referralState.isValidating
                ? "Validating referral..."
                : "Submit Bid Request"}
            </Button>
          )}
        </form>
      </Form>
    </div>
  );
}
